'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import AudioGenerationPanel from './AudioGenerationPanel';
import {
  Sparkles,
  Crown,
  Menu,
  X,
  Sun,
  Moon,
  Zap,
  Clock,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Lightbulb,
  Brain,
  Smile,
  FlaskConical,
  Headphones,
  Loader2,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import type { FullPlanOutput, MeditationScriptsOutput } from '@/lib/aiPersonalisationService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PracticeData {
  firstName: string;
  userId: string | null;
  tier: 'FREE' | 'PLUS' | 'PREMIUM';
  fullPlan: FullPlanOutput | null;
  scripts: MeditationScriptsOutput | null;
}

// ─── Accordion ────────────────────────────────────────────────────────────────

function Accordion({
  title,
  icon,
  badge,
  defaultOpen = false,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  badge?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card-elevated overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-5 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <div>
            <p className="font-bold text-[#1C1917] text-base">{title}</p>
            {badge && <p className="text-xs text-[#9B9590] mt-0.5">{badge}</p>}
          </div>
        </div>
        {open ? (
          <ChevronUp size={18} className="text-[#9B9590] flex-shrink-0" />
        ) : (
          <ChevronDown size={18} className="text-[#9B9590] flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-[#EAE6E0] pt-5">{children}</div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PracticeHomeContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [data, setData] = useState<PracticeData>({
    firstName: 'there',
    userId: null,
    tier: 'FREE',
    fullPlan: null,
    scripts: null,
  });

  useEffect(() => {
    async function initDashboard() {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // Not authenticated — redirect to plan preview
        router.replace('/personalised-plan-preview');
        return;
      }

      setAuthChecked(true);

      const fromMagicLink = searchParams?.get('from_magic_link') === '1';

      // On first login via magic link, persist the pending questionnaire data
      if (fromMagicLink) {
        try {
          const pendingAnswersRaw = localStorage.getItem('blv_pending_answers');
          const pendingEmail = localStorage.getItem('blv_pending_email') ?? user.email ?? '';
          const pendingAnswers = pendingAnswersRaw ? JSON.parse(pendingAnswersRaw) : null;

          if (pendingAnswers && pendingEmail) {
            setGeneratingPlan(true);

            // Extract first name from answers (q5_name) or fall back to email prefix
            const firstName = (pendingAnswers?.q5_name as string)?.trim().split(/\s+/)[0]
              || pendingEmail.split('@')[0]
              || 'there';

            const res = await fetch('/api/users/create', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                firstName,
                email: pendingEmail,
                questionnaireAnswers: pendingAnswers,
              }),
            });

            if (res.ok) {
              const result = await res.json().catch(() => ({}));
              if (result.fullPlan) {
                localStorage.setItem('blv_full_plan', JSON.stringify(result.fullPlan));
                sessionStorage.setItem('blv_full_plan', JSON.stringify(result.fullPlan));
              }
              if (result.scripts) {
                localStorage.setItem('blv_scripts', JSON.stringify(result.scripts));
                sessionStorage.setItem('blv_scripts', JSON.stringify(result.scripts));
              }
              if (result.userId) {
                localStorage.setItem('blv_user_id', result.userId);
                sessionStorage.setItem('blv_user_id', result.userId);
              }
              localStorage.setItem('blv_first_name', firstName);
              sessionStorage.setItem('blv_first_name', firstName);
            }

            // Clean up pending data
            localStorage.removeItem('blv_pending_answers');
            localStorage.removeItem('blv_pending_email');
            localStorage.removeItem('blv_pending_plan_preview');
            setGeneratingPlan(false);

            // Remove the query param without a full reload
            router.replace('/your-reset-practice-home');
            return;
          }
        } catch (err) {
          console.error('[blv] first-login plan persistence error:', err);
          setGeneratingPlan(false);
        }
      }

      // Load plan data from storage
      try {
        const planRaw = sessionStorage.getItem('blv_full_plan') ?? localStorage.getItem('blv_full_plan');
        const scriptsRaw = sessionStorage.getItem('blv_scripts') ?? localStorage.getItem('blv_scripts');
        const rawName = sessionStorage.getItem('blv_first_name') ?? localStorage.getItem('blv_first_name') ?? 'there';
        const userId = sessionStorage.getItem('blv_user_id') ?? localStorage.getItem('blv_user_id') ?? user.id;
        const firstName = rawName.trim().split(/\s+/)[0] || 'there';
        setData({
          firstName,
          userId,
          tier: 'FREE',
          fullPlan: planRaw ? (JSON.parse(planRaw) as FullPlanOutput) : null,
          scripts: scriptsRaw ? (JSON.parse(scriptsRaw) as MeditationScriptsOutput) : null,
        });
      } catch {
        // storage unavailable or parse error — use defaults
      }
    }

    initDashboard();
  }, [router, searchParams]);

  // Show loading while auth is being checked or plan is being generated
  if (!authChecked || generatingPlan) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#FAF8F5]">
        <AppLogo size={52} className="mb-8 pulse-soft" />
        <h2 className="text-2xl font-bold text-[#1C1917] mb-3 text-center">
          {generatingPlan ? 'Building your personalised plan…' : 'Loading your practice…'}
        </h2>
        {generatingPlan && (
          <p className="text-[#6B6560] text-center max-w-sm leading-relaxed mb-6">
            We&apos;re generating your morning and evening practices. This takes just a moment.
          </p>
        )}
        <Loader2 size={28} className="text-[#2A9D8F] animate-spin" />
      </div>
    );
  }

  const { firstName, userId, tier, fullPlan, scripts } = data;
  const plan = fullPlan;

  // Derive a "why we built this" narrative from the plan data
  const patternHeadline = plan?.pattern_summary?.headline ?? null;
  const patternSummary = plan?.pattern_summary?.summary_text ?? null;
  const scienceNarrative = (plan as (FullPlanOutput & { science_narrative?: string }) | null)?.science_narrative ?? null;

  const morningTitle = scripts?.morning_meditation?.title ?? 'Your morning meditation';
  const eveningTitle = scripts?.evening_meditation?.title ?? 'Your evening meditation';
  const morningDuration = `${scripts?.morning_meditation?.duration_minutes ?? 6} min`;
  const eveningDuration = `${scripts?.evening_meditation?.duration_minutes ?? 8} min`;

  return (
    <div className="min-h-screen">
      {/* Top navigation */}
      <nav className="sticky top-0 z-50 bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#EAE6E0]">
        <div className="max-w-screen-lg mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing-welcome" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-lg text-[#1C1917]">blv</span>
          </Link>
          <div className="hidden sm:flex items-center gap-6">
            <span className="text-sm font-semibold text-[#2A9D8F] border-b-2 border-[#2A9D8F] pb-0.5">
              My Practice
            </span>
            <Link href="/pricing-upgrade" className="text-sm font-medium text-[#6B6560] hover:text-[#2A9D8F] transition-colors">
              Plans
            </Link>
            <Link
              href="/pricing-upgrade"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B8860B] bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5 hover:bg-amber-100 transition-colors"
            >
              <Crown size={13} />
              Upgrade
            </Link>
          </div>
          <button
            className="sm:hidden p-2 rounded-xl hover:bg-[#EAE6E0] transition-colors"
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-[#EAE6E0] bg-white px-6 py-4 space-y-3">
            <Link href="/pricing-upgrade" className="block text-sm font-medium text-[#6B6560] py-2">Plans</Link>
            <Link href="/pricing-upgrade" className="block text-sm font-semibold text-[#B8860B] py-2">Upgrade to PLUS</Link>
          </div>
        )}
      </nav>

      <main className="max-w-screen-lg mx-auto px-6 py-10">
        {/* Welcome header */}
        <div className="mb-8">
          <p className="text-sm text-[#9B9590] mb-1">Good {getTimeOfDay()}, {firstName}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917] mb-2">
            Your Personalised Daily Reset Plan
          </h1>
          <p className="text-[#6B6560] text-sm leading-relaxed max-w-xl">
            Built around your goals, patterns, and pace. Evidence-informed guidance that adapts to you over time.
          </p>
        </div>

        <div className="space-y-5">

          {/* ── 1. WHY WE BUILT THIS PLAN FOR YOU (prominent, top) ─────────── */}
          <div className="rounded-2xl border border-[#2A9D8F]/20 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(42,157,143,0.06) 0%, rgba(82,183,136,0.04) 100%)' }}>
            <div className="px-6 pt-6 pb-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#2A9D8F]/15 flex items-center justify-center flex-shrink-0">
                  <Brain size={16} className="text-[#2A9D8F]" />
                </div>
                <div>
                  <p className="font-bold text-[#1C1917] text-base">Why we built this plan for you</p>
                  <p className="text-xs text-[#9B9590] mt-0.5">Your pattern, interpreted</p>
                </div>
              </div>

              {patternHeadline && (
                <p className="text-sm font-semibold text-[#2A9D8F] mb-3">{patternHeadline}</p>
              )}

              {patternSummary && (
                <p className="text-sm text-[#4A4540] leading-relaxed mb-4">
                  {patternSummary}
                </p>
              )}

              {!patternSummary && (
                <p className="text-sm text-[#4A4540] leading-relaxed mb-4">
                  Every section of your practice has been shaped around your specific pattern, goals, and the context you shared. The tools you&apos;ll find here are not generic wellness advice — they are the specific interventions most likely to help someone with your profile, grounded in evidence and designed to fit your real life.
                </p>
              )}

              {scienceNarrative && (
                <div className="rounded-xl bg-white/60 border border-[#EAE6E0] p-4 mb-4">
                  <p className="text-xs font-semibold text-[#9B9590] uppercase tracking-widest mb-2">The science behind your plan</p>
                  <p className="text-sm text-[#4A4540] leading-relaxed">{scienceNarrative}</p>
                </div>
              )}

              {/* Tags */}
              {plan?.pattern_summary?.tags && plan.pattern_summary.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 pb-5">
                  {plan.pattern_summary.tags.map((tag) => (
                    <span key={`home-tag-${tag}`} className="tag-chip">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── 2. WHY THIS WORKS (prominent, second) ──────────────────────── */}
          {plan?.why_this_works && plan.why_this_works.length > 0 && (
            <div className="card-elevated overflow-hidden">
              <div className="px-6 pt-6 pb-1">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-[#52B788]/10 flex items-center justify-center flex-shrink-0">
                    <Lightbulb size={16} className="text-[#52B788]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#1C1917] text-base">Why this works for you</p>
                    <p className="text-xs text-[#9B9590] mt-0.5">Evidence-informed explanations</p>
                  </div>
                </div>
                <div className="space-y-4 pb-5">
                  {plan.why_this_works.map((item, i) => (
                    <div key={`why-${i}`} className="flex gap-3">
                      <CheckCircle2 size={16} className="text-[#52B788] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-[#1C1917] mb-0.5">{item.title}</p>
                        <p className="text-sm text-[#6B6560] leading-relaxed">{item.explanation}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── 3. Science of positive self-cueing callout ─────────────────── */}
          <div className="rounded-2xl p-5 border border-[#52B788]/25 bg-gradient-to-br from-[#52B788]/06 to-[#2A9D8F]/04">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#52B788]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Smile size={18} className="text-[#52B788]" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-bold text-[#1C1917]">The science of positive self-cueing</p>
                  <span className="text-xs font-medium text-[#52B788] bg-[#52B788]/10 px-2 py-0.5 rounded-full">Core blv principle</span>
                </div>
                <p className="text-sm text-[#4A4540] leading-relaxed mb-3">
                  Research in facial feedback theory (Strack et al.) shows that introducing a deliberate smile — even when you don&apos;t feel like it — activates the same neural pathways as genuine positive emotion. Your face doesn&apos;t just express how you feel; it helps create how you feel.
                </p>
                <p className="text-sm text-[#4A4540] leading-relaxed mb-3">
                  The most powerful application is using this cue precisely when you least feel like it: in a moment of self-doubt, frustration, or low mood. This is not forced positivity. It is a neurological interrupt — a signal to your nervous system that you are safe and capable.
                </p>
                <div className="rounded-xl bg-white/70 border border-[#EAE6E0] p-3">
                  <p className="text-xs font-semibold text-[#2A9D8F] mb-1">The triple cue</p>
                  <p className="text-xs text-[#6B6560] leading-relaxed">
                    <strong>Breath + Smile + Belief.</strong> Every reset in your plan pairs a slow breath with a deliberate smile and a brief self-affirming thought. This triple cue becomes more effective with repetition — your nervous system learns to associate it with safety and capability.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── 4. Pattern Summary (accordion) ─────────────────────────────── */}
          <Accordion
            title={plan?.pattern_summary?.headline ?? 'Your pattern'}
            icon={<Sparkles size={16} className="text-[#2A9D8F]" />}
            defaultOpen={false}
            badge="Your personalised pattern analysis"
          >
            <div className="flex flex-wrap gap-2 mb-4">
              {(plan?.pattern_summary?.tags ?? ['personalised practice']).map((tag) => (
                <span key={`tag-${tag}`} className="tag-chip">{tag}</span>
              ))}
            </div>
            <p className="text-[#4A4540] leading-relaxed text-sm">
              {plan?.pattern_summary?.summary_text ??
                'Your personalised practice has been shaped around your responses. Explore the sections below to see your full plan.'}
            </p>
          </Accordion>

          {/* ── 5. 7-Day Starter Plan ──────────────────────────────────────── */}
          <Accordion
            title="Your 7-day starter plan"
            icon={<BookOpen size={16} className="text-[#2A9D8F]" />}
            badge={plan?.starter_plan?.overview ?? 'A week of focused, gentle practice'}
            defaultOpen={true}
          >
            <div className="space-y-3">
              {(plan?.starter_plan?.day_1_to_7_focus ?? []).map((d) => (
                <div key={`day-${d.day}`} className="flex gap-3 items-start">
                  <div className="w-7 h-7 rounded-full bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-[#2A9D8F]">{d.day}</span>
                  </div>
                  <p className="text-sm text-[#4A4540] leading-relaxed pt-0.5">{d.focus}</p>
                </div>
              ))}
            </div>
          </Accordion>

          {/* ── 6. Morning Plan ────────────────────────────────────────────── */}
          <Accordion
            title={plan?.morning_plan?.title ?? 'Your morning practice'}
            icon={<Sun size={16} className="text-amber-500" />}
            badge={plan?.morning_plan ? `${plan.morning_plan.duration_minutes} min · ${plan.morning_plan.goal}` : '6 min · Start the day with intention'}
          >
            {plan?.morning_plan?.why && (
              <p className="text-xs text-[#9B9590] italic mb-4 pb-4 border-b border-[#EAE6E0]">
                {plan.morning_plan.why}
              </p>
            )}
            <ol className="space-y-3">
              {(plan?.morning_plan?.steps ?? []).map((step, i) => (
                <li key={`morning-step-${i}`} className="flex gap-3 items-start">
                  <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-[#4A4540] leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          </Accordion>

          {/* ── 7. Evening Plan ────────────────────────────────────────────── */}
          <Accordion
            title={plan?.evening_plan?.title ?? 'Your evening practice'}
            icon={<Moon size={16} className="text-indigo-400" />}
            badge={plan?.evening_plan ? `${plan.evening_plan.duration_minutes} min · ${plan.evening_plan.goal}` : '8 min · Wind down and release the day'}
          >
            {plan?.evening_plan?.why && (
              <p className="text-xs text-[#9B9590] italic mb-4 pb-4 border-b border-[#EAE6E0]">
                {plan.evening_plan.why}
              </p>
            )}
            <ol className="space-y-3">
              {(plan?.evening_plan?.steps ?? []).map((step, i) => (
                <li key={`evening-step-${i}`} className="flex gap-3 items-start">
                  <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-[#4A4540] leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          </Accordion>

          {/* ── 8. Throughout-the-Day Tools ────────────────────────────────── */}
          <Accordion
            title="Throughout-the-day tools"
            icon={<Zap size={16} className="text-[#2A9D8F]" />}
            badge="Quick resets for any moment"
            defaultOpen={true}
          >
            <div className="space-y-5">
              {/* 10-second cue */}
              <div className="reset-card">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0">
                    <Zap size={13} className="text-[#2A9D8F]" />
                  </div>
                  <p className="text-xs font-semibold text-[#2A9D8F] uppercase tracking-widest">
                    {plan?.throughout_day_tools?.ten_second_cue?.title ?? '10-second reset'}
                  </p>
                </div>
                <p className="text-sm text-[#4A4540] leading-relaxed mb-2">
                  {plan?.throughout_day_tools?.ten_second_cue?.instruction ??
                    'Take one slow breath. Feel your feet on the floor. Allow a deliberate smile.'}
                </p>
                {plan?.throughout_day_tools?.ten_second_cue?.why && (
                  <p className="text-xs text-[#9B9590] italic">
                    {plan.throughout_day_tools.ten_second_cue.why}
                  </p>
                )}
              </div>

              {/* 60-second reset */}
              <div className="reset-card">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg bg-[#52B788]/10 flex items-center justify-center flex-shrink-0">
                    <Clock size={13} className="text-[#52B788]" />
                  </div>
                  <p className="text-xs font-semibold text-[#52B788] uppercase tracking-widest">
                    {plan?.throughout_day_tools?.sixty_second_reset?.title ?? '60-second reset'}
                  </p>
                </div>
                <p className="text-sm text-[#4A4540] leading-relaxed mb-2">
                  {plan?.throughout_day_tools?.sixty_second_reset?.instruction ??
                    'Find a comfortable position. Take five slow breaths. Return to your day feeling settled.'}
                </p>
                {plan?.throughout_day_tools?.sixty_second_reset?.why && (
                  <p className="text-xs text-[#9B9590] italic">
                    {plan.throughout_day_tools.sixty_second_reset.why}
                  </p>
                )}
              </div>

              {/* If-then plan */}
              {plan?.throughout_day_tools?.if_then_plan && (
                <div className="rounded-2xl p-4 border border-[#2A9D8F]/20" style={{ background: 'rgba(42,157,143,0.04)' }}>
                  <p className="text-xs font-semibold text-[#2A9D8F] uppercase tracking-widest mb-2">
                    Your implementation intention
                  </p>
                  <p className="text-sm font-medium text-[#1C1917] leading-relaxed mb-2 italic">
                    &ldquo;{plan.throughout_day_tools.if_then_plan.full_statement}&rdquo;
                  </p>
                  {plan.throughout_day_tools.if_then_plan.why && (
                    <p className="text-xs text-[#9B9590] leading-relaxed">
                      {plan.throughout_day_tools.if_then_plan.why}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Accordion>

          {/* ── 9. Personalised Audio (NEW) ────────────────────────────────── */}
          <div className="card-elevated overflow-hidden">
            <div className="px-6 pt-6 pb-6">
              {userId ? (
                <AudioGenerationPanel
                  userId={userId}
                  morningTitle={morningTitle}
                  eveningTitle={eveningTitle}
                  morningDuration={morningDuration}
                  eveningDuration={eveningDuration}
                />
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-9 h-9 rounded-xl bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0">
                      <Headphones size={16} className="text-[#2A9D8F]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#1C1917] text-base">Your personalised audio</p>
                      <p className="text-xs text-[#9B9590] mt-0.5">Complete your account to access audio meditations</p>
                    </div>
                  </div>
                  <p className="text-sm text-[#6B6560] leading-relaxed">
                    Your morning and evening meditations will be available as personalised audio once your account is set up.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ── 10. Morning Meditation Script ──────────────────────────────── */}
          <Accordion
            title={scripts?.morning_meditation?.title ?? 'Your morning meditation'}
            icon={<Sun size={16} className="text-amber-500" />}
            badge={scripts?.morning_meditation ? `${scripts.morning_meditation.duration_minutes} min · ${scripts.morning_meditation.purpose}` : '6 min · Guided morning practice'}
          >
            {scripts?.morning_meditation?.script_text && (
              <div>
                <p className="text-xs font-semibold text-[#9B9590] uppercase tracking-widest mb-3">
                  Your meditation script
                </p>
                <div className="prose prose-sm max-w-none text-[#4A4540] leading-relaxed space-y-3">
                  {scripts.morning_meditation.script_text.split('\n\n').map((para, i) => (
                    <p key={`morning-script-${i}`}>{para}</p>
                  ))}
                </div>
              </div>
            )}
          </Accordion>

          {/* ── 11. Evening Meditation Script ──────────────────────────────── */}
          <Accordion
            title={scripts?.evening_meditation?.title ?? 'Your evening meditation'}
            icon={<Moon size={16} className="text-indigo-400" />}
            badge={scripts?.evening_meditation ? `${scripts.evening_meditation.duration_minutes} min · ${scripts.evening_meditation.purpose}` : '8 min · Guided evening practice'}
          >
            {scripts?.evening_meditation?.script_text && (
              <div>
                <p className="text-xs font-semibold text-[#9B9590] uppercase tracking-widest mb-3">
                  Your meditation script
                </p>
                <div className="prose prose-sm max-w-none text-[#4A4540] leading-relaxed space-y-3">
                  {scripts.evening_meditation.script_text.split('\n\n').map((para, i) => (
                    <p key={`evening-script-${i}`}>{para}</p>
                  ))}
                </div>
              </div>
            )}
          </Accordion>

          {/* ── Science deep-dive accordion ────────────────────────────────── */}
          <Accordion
            title="The science behind your plan"
            icon={<FlaskConical size={16} className="text-amber-600" />}
            badge="Evidence-informed principles"
          >
            <div className="space-y-5">
              <div>
                <p className="text-sm font-semibold text-[#1C1917] mb-1">Facial feedback theory &amp; the deliberate smile</p>
                <p className="text-sm text-[#6B6560] leading-relaxed">Research by Strack et al. (replicated across multiple studies) demonstrates that the act of smiling — even deliberately, even when you don&apos;t feel like it — activates the same neural pathways as genuine positive emotion. The face doesn&apos;t just express how you feel; it helps create how you feel. This is why every reset in your plan includes a deliberate smile, especially in moments of low mood or self-doubt.</p>
              </div>
              <div className="border-t border-[#EAE6E0]" />
              <div>
                <p className="text-sm font-semibold text-[#1C1917] mb-1">Embodied cognition (Niedenthal, Barsalou)</p>
                <p className="text-sm text-[#6B6560] leading-relaxed">Physical states influence cognitive states — posture, breath, and facial expression all shape how we think and feel, not just the other way around. This is the foundation of blv&apos;s approach: working with the body to shift the mind, rather than trying to think your way out of a feeling.</p>
              </div>
              <div className="border-t border-[#EAE6E0]" />
              <div>
                <p className="text-sm font-semibold text-[#1C1917] mb-1">Implementation intentions (Gollwitzer)</p>
                <p className="text-sm text-[#6B6560] leading-relaxed">Deciding in advance exactly when and how you will act — &ldquo;if X happens, then I will do Y&rdquo; — significantly increases follow-through compared to vague intentions. This is one of the most replicated findings in behaviour change research. Your if-then plan is built specifically around your trigger situation.</p>
              </div>
              <div className="border-t border-[#EAE6E0]" />
              <div>
                <p className="text-sm font-semibold text-[#1C1917] mb-1">Brief, consistent practice beats occasional long sessions</p>
                <p className="text-sm text-[#6B6560] leading-relaxed">Research consistently shows that short, daily practices build stronger habits and produce more lasting change than occasional longer sessions. Your plan is designed around this: small, achievable tools that fit your real life and compound over time.</p>
              </div>
            </div>
          </Accordion>

          {/* ── Upgrade prompt ─────────────────────────────────────────────── */}
          {tier === 'FREE' && (
            <div
              className="rounded-2xl p-6 border border-amber-200"
              style={{ background: 'rgba(251,191,36,0.05)' }}
            >
              <div className="flex items-start gap-3">
                <Crown size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-[#1C1917] mb-1">Unlock your full practice</h4>
                  <p className="text-sm text-[#6B6560] leading-relaxed mb-3">
                    PLUS members get evolving practices that update as blv learns how you use it,
                    daily WhatsApp resets, and 12 additional sessions tailored to your pattern.
                  </p>
                  <Link href="/pricing-upgrade" className="btn-primary text-sm inline-flex" style={{ minHeight: '40px', padding: '0 1.25rem' }}>
                    <Crown size={14} />
                    See PLUS plans
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}